/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Operations › Analytics (the usage analytics contract, section 5): what gets used, by whom, how often, where people go
 * next and who comes back, collected by Gyro itself. One page: a lens in the header (the window, the surface, the three
 * audience presets and the "Admin screens hidden/shown" chip), a section strip (Realtime, Overview, Screens, Actions,
 * Sessions, Journeys, Retention, People, Tech, and Cost: the token and cost views the Usage page held before), and the
 * section on screen. Every section reads with the lens; a section that ignored it would be a bug.
 *
 * The window and surface live in the address (`?view=screens&window=90&surface=web`), so a link carries them; the audience
 * preset and the chip are remembered per device (localStorage, lib/usage.ts), the preset name following the audience alone.
 */
import { useRef, useState, type KeyboardEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import {
  AUDIENCE_PRESETS, PRESET_LABELS, SURFACE_LABELS, USAGE_SURFACES, USAGE_WINDOWS, lensFromSearch, lensQuery, lensSearch, presetOf, readStoredLens, usageQueryString, withPreset, writeStoredLens,
  type AudiencePreset, type UsageLens, type UsageSurface, type UsageWindow,
} from '../lib/usage';
import { navigate, usePlace } from '../shell/router';
import { Button, PageHeader, Segmented, Select, Stack } from '../ui';
import { UsageRealtime } from './usage/Realtime';
import { UsageOverview } from './usage/Overview';
import { UsageFeatures } from './usage/Features';
import { UsageSessions } from './usage/Sessions';
import { UsageJourneys } from './usage/Journeys';
import { UsageRetention } from './usage/Retention';
import { UsagePeople } from './usage/People';
import { UsageTech } from './usage/Tech';
import { UsageCost } from './usage/Cost';
import type { SectionProps } from './usage/parts';

export type AnalyticsView = 'realtime' | 'overview' | 'screens' | 'actions' | 'sessions' | 'journeys' | 'retention' | 'people' | 'tech' | 'cost';
export const ANALYTICS_VIEWS: readonly AnalyticsView[] = ['realtime', 'overview', 'screens', 'actions', 'sessions', 'journeys', 'retention', 'people', 'tech', 'cost'];
export const VIEW_LABELS: Record<AnalyticsView, string> = {
  realtime: 'Realtime', overview: 'Overview', screens: 'Screens', actions: 'Actions', sessions: 'Sessions', journeys: 'Journeys', retention: 'Retention', people: 'People', tech: 'Tech', cost: 'Cost',
};
export const DEFAULT_VIEW: AnalyticsView = 'overview';
const isView = (value: string | null): value is AnalyticsView => (ANALYTICS_VIEWS as readonly string[]).includes(value ?? '');

const WINDOW_OPTIONS = USAGE_WINDOWS.map((window) => ({ value: String(window), label: `${window} days` }));
const PRESET_OPTIONS = AUDIENCE_PRESETS.map((preset) => ({ value: preset, label: PRESET_LABELS[preset] }));

const storage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

export function Analytics({ canEdit }: { canEdit: boolean }) {
  const place = usePlace();
  const [stored, setStored] = useState(() => readStoredLens(storage()));
  const params = new URLSearchParams(place.search);
  const view: AnalyticsView = isView(params.get('view')) ? (params.get('view') as AnalyticsView) : DEFAULT_VIEW;
  const lens: UsageLens = lensFromSearch(place.search, stored);
  const query = lensQuery(lens);
  const lensKey = usageQueryString(query);

  const setAddress = (next: Pick<UsageLens, 'window' | 'surface'>, nextView: AnalyticsView = view) => {
    const others = new URLSearchParams(place.search);
    if (nextView === DEFAULT_VIEW) others.delete('view');
    else others.set('view', nextView);
    navigate(`${place.pathname}${lensSearch(next, others)}`, { replace: true });
  };
  const setAudience = (next: UsageLens) => {
    const half = { audience: next.audience, hideAdmin: next.hideAdmin };
    setStored(half);
    writeStoredLens(storage(), half);
  };
  const pickPreset = (preset: AudiencePreset) => setAudience(withPreset(lens, preset));
  const toggleHidden = () => setAudience({ ...lens, hideAdmin: !lens.hideAdmin });
  const pickView = (next: AnalyticsView) => setAddress({ window: lens.window, surface: lens.surface }, next);

  /** The strip's keyboard: Left and Right move to the section before or after (round), Home and End to the first or last. */
  const tabRefs = useRef(new Map<AnalyticsView, HTMLButtonElement>());
  const onTabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const at = ANALYTICS_VIEWS.indexOf(view);
    const next = event.key === 'ArrowRight' ? (at + 1) % ANALYTICS_VIEWS.length
      : event.key === 'ArrowLeft' ? (at - 1 + ANALYTICS_VIEWS.length) % ANALYTICS_VIEWS.length
        : event.key === 'Home' ? 0
          : event.key === 'End' ? ANALYTICS_VIEWS.length - 1
            : -1;
    if (next < 0) return;
    event.preventDefault();
    const id = ANALYTICS_VIEWS[next]!;
    pickView(id);
    tabRefs.current.get(id)?.focus();
  };

  const sectionProps: SectionProps = { lens, query, lensKey };
  let content;
  switch (view) {
    case 'realtime': content = <UsageRealtime {...sectionProps} />; break;
    case 'screens': content = <UsageFeatures kind="screen" {...sectionProps} />; break;
    case 'actions': content = <UsageFeatures kind="action" {...sectionProps} />; break;
    case 'sessions': content = <UsageSessions {...sectionProps} />; break;
    case 'journeys': content = <UsageJourneys {...sectionProps} />; break;
    case 'retention': content = <UsageRetention {...sectionProps} />; break;
    case 'people': content = <UsagePeople {...sectionProps} canEdit={canEdit} />; break;
    case 'tech': content = <UsageTech {...sectionProps} />; break;
    case 'cost': content = <UsageCost />; break;
    default: content = <UsageOverview {...sectionProps} />;
  }

  return (
    <>
      <PageHeader
        meta={<span>What gets used, by whom, how often, and where people go next. Collected by Gyro itself; counts only, never content.</span>}
        actions={(
          <div className="usage-lens" aria-label="Lens">
            <Segmented label="Window" options={WINDOW_OPTIONS} value={String(lens.window)} onChange={(value) => setAddress({ window: Number(value) as UsageWindow, surface: lens.surface })} />
            <Select size="sm" className="ui-w-auto" aria-label="Surface" value={lens.surface} onChange={(event) => setAddress({ window: lens.window, surface: event.target.value as UsageSurface })}>
              {USAGE_SURFACES.map((surface) => <option key={surface} value={surface}>{SURFACE_LABELS[surface]}</option>)}
            </Select>
            <Segmented label="Audience" options={PRESET_OPTIONS} value={presetOf(lens)} onChange={pickPreset} />
            <Button
              size="sm"
              variant="ghost"
              icon={lens.hideAdmin ? EyeOff : Eye}
              aria-pressed={lens.hideAdmin}
              title={lens.hideAdmin ? 'The admin area’s own screens and actions are left out of rankings, journeys, realtime screens and top screens. Click to show them.' : 'The admin area’s own screens and actions are counted. Click to hide them.'}
              onClick={toggleHidden}
            >
              {lens.hideAdmin ? 'Admin screens hidden' : 'Admin screens shown'}
            </Button>
          </div>
        )}
      />
      <div className="ui-page">
        <Stack>
          <div role="tablist" aria-label="Analytics sections" className="ui-tabbar">
            {ANALYTICS_VIEWS.map((id) => (
              <button
                key={id}
                ref={(element) => {
                  if (element) tabRefs.current.set(id, element);
                  else tabRefs.current.delete(id);
                }}
                type="button"
                role="tab"
                id={`analytics-tab-${id}`}
                aria-selected={view === id}
                aria-controls="analytics-section"
                tabIndex={view === id ? 0 : -1}
                className={`ui-tab${view === id ? ' is-active' : ''}`}
                onClick={() => pickView(id)}
                onKeyDown={onTabKey}
              >
                {VIEW_LABELS[id]}
              </button>
            ))}
          </div>
          <div id="analytics-section" role="tabpanel" tabIndex={0} aria-labelledby={`analytics-tab-${view}`}>
            {content}
          </div>
        </Stack>
      </div>
    </>
  );
}
